<template>
  <div
    v-if="visible"
    ref="menu"
    class="dropdown-menu show position-fixed"
    :style="{ left: `${x}px`, top: `${y}px` }"
  >
    <button class="dropdown-item d-flex align-items-center" @click="showCoordinates">
      <span class="material-icons mr-1">my_location</span>
      Show Coordinates
    </button>
    <button class="dropdown-item d-flex align-items-center" @click="addCustomPOI">
      <span class="material-icons mr-1">star</span>
      Add Hike Highlight
    </button>
    <button
      v-if="inactiveRoute"
      class="dropdown-item d-flex align-items-center"
      @click="activateRoute"
    >
      <span class="material-icons mr-1">edit</span>
      Activate Route
    </button>
    <button
      v-if="hoveredMarker && canSplitHere"
      class="dropdown-item d-flex align-items-center"
      @click="splitRoute"
    >
      <span class="material-icons mr-1">arrow_split</span>
      Split Route Here
    </button>
    <button
      v-if="hoveredMarker"
      class="dropdown-item d-flex align-items-center"
      @click="addCustomCue"
    >
      <span class="material-icons mr-1">alt_route</span>
      Add turn-by-turn prompt
    </button>
    <button
      v-if="hoveredMarker"
      class="dropdown-item d-flex align-items-center text-danger"
      @click="removeMarker"
    >
      <span class="material-icons mr-1">delete</span>
      Remove Marker
    </button>
  </div>

  <!-- Coordinate Modal -->
  <div v-if="showCoordModal" class="modal show d-block" tabindex="-1" @click="closeCoordModal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h5 class="modal-title">Coordinates</h5>
          <button type="button" class="btn-close" @click="closeCoordModal"></button>
        </div>
        <div class="modal-body">
          <div class="row mb-2">
            <div class="col-4 fw-bold">Latitude:</div>
            <div class="col-8 font-monospace">{{ coordinates.lat.toFixed(6) }}</div>
          </div>
          <div class="row">
            <div class="col-4 fw-bold">Longitude:</div>
            <div class="col-8 font-monospace">{{ coordinates.lng.toFixed(6) }}</div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeCoordModal">
            Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="copyCoordinates">
            {{ copyButtonText }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="showCoordModal" class="modal-backdrop show"></div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
  name: 'MapContextMenu',
  data() {
    return {
      visible: false,
      x: 0,
      y: 0,
      coordinates: { lat: 0, lng: 0 },
      hoveredMarker: null,
      inactiveRoute: null,
      showCoordModal: false,
      copyButtonText: 'Copy'
    };
  },
  computed: {
    ...mapState('planTrail', ['routes', 'activeRouteKey']),
    canSplitHere() {
      if (!this.hoveredMarker) return false;
      const { routeId, markerIndex } = this.hoveredMarker;
      const routeKey = Object.keys(this.routes).find(key => this.routes[key].id === routeId);
      if (!routeKey) return false;
      const route = this.routes[routeKey];
      if (!route || !route.markers) return false;
      return markerIndex > 0 && markerIndex < route.markers.length - 1;
    }
  },
  mounted() {
    // Listen for context menu events from the map
    // In Vue 3, we'll use a global event emitter or props/emit pattern
    window.addEventListener('map-context-menu', this.handleMapContextMenu);
    document.addEventListener('click', this.hideMenu);
    document.addEventListener('contextmenu', this.handleDocumentContextMenu);
  },
  beforeUnmount() {
    window.removeEventListener('map-context-menu', this.handleMapContextMenu);
    document.removeEventListener('click', this.hideMenu);
    document.removeEventListener('contextmenu', this.handleDocumentContextMenu);
  },
  methods: {
    ...mapActions('planTrail', ['removeMarkerById', 'splitRouteAtMarker']),

    handleMapContextMenu(event) {
      this.showMenu(event.detail);
    },

    showMenu(event) {
      const { x, y, lngLat, features } = event;

      // Position the menu with a small offset from click location
      // This makes it feel more natural and prevents the menu from appearing under the cursor
      const offset = 2; // pixels
      this.x = x + offset;
      this.y = y + offset;
      this.coordinates = lngLat;

      // Check if a marker was clicked
      this.hoveredMarker = this.findMarkerAtPoint(features);

      // Check if an inactive route line was clicked
      this.inactiveRoute = this.findInactiveRouteAtPoint(features);

      this.visible = true;
      window.mapContextMenuVisible = true; // Set global flag

      // Ensure menu stays within viewport
      this.$nextTick(() => {
        if (this.$refs.menu) {
          const rect = this.$refs.menu.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;

          // Adjust if menu would go off the right edge
          if (rect.right > viewportWidth) {
            this.x = x - rect.width - offset;
          }

          // Adjust if menu would go off the bottom edge
          if (rect.bottom > viewportHeight) {
            this.y = y - rect.height - offset;
          }
        }
      });
    },

    hideMenu() {
      this.visible = false;
      this.hoveredMarker = null;
      this.inactiveRoute = null;
      window.mapContextMenuVisible = false; // Clear global flag
    },

    handleDocumentContextMenu(e) {
      // Prevent default context menu outside of map
      if (!e.target.closest('.mapboxgl-canvas')) {
        this.hideMenu();
      }
    },

    findMarkerAtPoint(features) {
      if (!features || features.length === 0) return null;

      // Look for marker features
      const markerFeature = features.find(f =>
        f.layer && f.layer.id && (
          f.layer.id.includes('-marker-') || f.layer.id === 'consolidated-routes-marker-layer'
        )
      );

      if (markerFeature && markerFeature.properties) {
        return {
          routeId: markerFeature.properties.routeId,
          markerIndex: markerFeature.properties.markerIndex,
          markerId: markerFeature.properties.markerId
        };
      }

      return null;
    },

    findInactiveRouteAtPoint(features) {
      if (!features || features.length === 0) return null;

      // Look for inactive route line features
      const inactiveRouteFeature = features.find(f =>
        f.layer && f.layer.id && (
          f.layer.id === 'consolidated-inactive-routes-lines' ||
          f.layer.id === 'consolidated-inactive-routes-borders'
        )
      );

      if (inactiveRouteFeature && inactiveRouteFeature.properties) {
        const routeKey = inactiveRouteFeature.properties.routeKey;
        const route = this.routes[routeKey];

        return {
          routeKey: routeKey,
          routeId: inactiveRouteFeature.properties.routeId,
          routeName: route?.name || 'Unnamed Route'
        };
      }

      return null;
    },

    showCoordinates() {
      this.showCoordModal = true;
      this.hideMenu();
    },

    closeCoordModal() {
      this.showCoordModal = false;
      this.copyButtonText = 'Copy';
    },

    async copyCoordinates() {
      const text = `${this.coordinates.lng.toFixed(6)}, ${this.coordinates.lat.toFixed(6)}`;

      try {
        await navigator.clipboard.writeText(text);
        this.copyButtonText = 'Copied!';
        setTimeout(() => {
          this.copyButtonText = 'Copy';
        }, 2000);
      } catch (err) {
        console.error('Failed to copy coordinates:', err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          this.copyButtonText = 'Copied!';
          setTimeout(() => {
            this.copyButtonText = 'Copy';
          }, 2000);
        } catch (err2) {
          console.error('Fallback copy failed:', err2);
        }
        document.body.removeChild(textArea);
      }
    },

    async removeMarker() {
      if (!this.hoveredMarker) return;

      const { routeId, markerIndex } = this.hoveredMarker;

      // Find the route
      const routeKey = Object.keys(this.routes).find(key =>
        this.routes[key].id === routeId
      );

      if (routeKey) {
        await this.removeMarkerById({
          routeKey,
          markerIndex
        });
      }

      this.hideMenu();
    },

    async splitRoute() {
      if (!this.hoveredMarker) return;
      const { routeId, markerIndex } = this.hoveredMarker;
      await this.splitRouteAtMarker({ routeId, markerIndex });
      this.hideMenu();
    },

    /**
     * A cue belongs to a control point, not to a place on the map — it is written into the
     * turns of the segment that starts there, so the marker is what identifies it.
     */
    addCustomCue() {
      if (!this.hoveredMarker) return;
      const { routeId, markerIndex } = this.hoveredMarker;
      this.$emit('add-custom-cue', { routeId, markerIndex });
      this.hideMenu();
    },

    addCustomPOI() {
      // Emit an event with the coordinates where the POI should be added
      this.$emit('add-custom-poi', {
        coordinates: this.coordinates,
        lngLat: this.coordinates
      });

      // You could also dispatch a Vuex action if preferred
      // this.$store.dispatch('planTrail/addCustomPOI', {
      //   coordinates: this.coordinates
      // });

      this.hideMenu();
    },

    async activateRoute() {
      if (!this.inactiveRoute) return;

      const { routeId } = this.inactiveRoute;

      // Switch to the selected route
      await this.$store.dispatch('planTrail/switchToRoute', routeId);

      this.hideMenu();
    }
  }
};
</script>

<style scoped>
/* Override Bootstrap dropdown-menu positioning since we handle it manually */
.dropdown-menu {
  position: fixed !important;
  margin: 0;
}

.dropdown-item .material-icons{
  font-size: 20px;
}

</style>
